/**
 * Company: SPHMedia
 * Description: [Translated Article Modal]
 */

import TranslateModalClose from "@assets/article/translateModalClose.svg";
import { StandFirst } from "@components/Elements/Typography/Paragraph";
import { FieldWrapper } from "@components/Modules/Translation/Elements/FieldWrapper";
import TranslationImage from "@components/Modules/Translation/Elements/TranslationImage";
import { pubPageAdTargeting } from "@config/ads";
import {
  TranslationAdsImu1,
  TranslationAdsMidArticle,
} from "@elements/Advertisement/variant/TranslationAds";
import Heading from "@elements/Typography/Heading";
import {
  AdTargetingType,
  ArticleAdTargetKeys,
  PageAdTargetingTypeEnum,
} from "@typings/Ads.d";
import Dompurify from "dompurify";
import React, { useEffect, useRef, useState } from "react";
import { ReactElement } from "react";

export type TranslatedArticleModalProps = {
  id: string;
  title: string;
  sectionName: string;
  adsSectionName: string;
  standFirst?: string;
  body: string[];
  imageUrl?: string;
  isOpen: boolean;
  onClose: () => void;
  gsChannels?: string[];
  articleSlotTarget?: AdTargetingType<ArticleAdTargetKeys, string>[];
  pageNumber: number;
};

type QuizElementId = "collapsed-quiz" | "expanded-quiz";

type QuizExpandedChangeDetail = {
  quizExpanded?: boolean;
};

/**
 * TranslatedArticleModal
 * Description: [Opens translated content of article as a modal]
 * @param props - The props required by the component
 * @returns The rendered React element
 */
export default function TranslatedArticleModal(
  props: TranslatedArticleModalProps,
): ReactElement {
  const {
    id,
    title,
    sectionName,
    adsSectionName,
    standFirst,
    body,
    imageUrl,
    onClose,
    gsChannels,
    articleSlotTarget,
    pageNumber,
  } = props;
  const [quizHeight, setQuizHeight] = useState<number>(0);
  const initialMeasureTimeoutRef = useRef<number | undefined>(undefined);
  const activeQuizElementIdRef = useRef<QuizElementId>("collapsed-quiz");
  const pageAdTarget = PageAdTargetingTypeEnum.TRANSLATION;
  const PubPageAdTarget = pubPageAdTargeting[pageAdTarget] as AdTargetingType<
    ArticleAdTargetKeys,
    string
  >;

  useEffect(() => {
    const getInitialQuizElementIdFromCookie = (): QuizElementId | undefined => {
      const quizCookie = document.cookie
        .split(";")
        .map((cookie) => cookie.trim())
        .find((cookie) => cookie.startsWith("puzzle_popup_collapsed="));

      if (!quizCookie) {
        return undefined;
      }

      const collapsedValue = quizCookie.split("=")[1];

      return collapsedValue === "0" ? "expanded-quiz" : "collapsed-quiz";
    };

    const getElementHeight = (id: QuizElementId) => {
      const element = document.getElementById(id);
      return element ? element.getBoundingClientRect().height : 0;
    };

    const getAvailableQuizElementId = (preferredId?: QuizElementId) => {
      if (preferredId && document.getElementById(preferredId)) {
        return preferredId;
      }

      if (document.getElementById(activeQuizElementIdRef.current)) {
        return activeQuizElementIdRef.current;
      }

      const collapsedQuizElement = document.getElementById("collapsed-quiz");
      const expandedQuizElement = document.getElementById("expanded-quiz");

      if (expandedQuizElement) {
        return "expanded-quiz";
      }

      if (collapsedQuizElement) {
        return "collapsed-quiz";
      }

      return "collapsed-quiz";
    };

    const measureQuizHeightForId = (preferredId?: QuizElementId) => {
      const elementId = getAvailableQuizElementId(preferredId);
      activeQuizElementIdRef.current = elementId;
      setQuizHeight(getElementHeight(elementId));
    };

    const measureQuizHeight = () => {
      measureQuizHeightForId();
    };

    const handleQuizChange = (event: Event) => {
      const customEvent = event as CustomEvent<QuizExpandedChangeDetail>;
      const nextQuizElementId: QuizElementId = customEvent.detail.quizExpanded
        ? "expanded-quiz"
        : "collapsed-quiz";

      // Measure immediately from event payload; ResizeObserver will track ongoing transition.
      measureQuizHeightForId(nextQuizElementId);
    };

    const initialQuizElementId = getInitialQuizElementIdFromCookie();

    if (initialQuizElementId) {
      activeQuizElementIdRef.current = initialQuizElementId;
    }

    measureQuizHeightForId(initialQuizElementId);

    window.addEventListener("resize", measureQuizHeight);
    window.addEventListener("quizExpandedChange", handleQuizChange);

    const collapsedQuizElement = document.getElementById("collapsed-quiz");
    const expandedQuizElement = document.getElementById("expanded-quiz");
    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(measureQuizHeight)
        : null;

    if (resizeObserver) {
      if (collapsedQuizElement) {
        resizeObserver.observe(collapsedQuizElement);
      }
      if (expandedQuizElement) {
        resizeObserver.observe(expandedQuizElement);
      }
    }

    initialMeasureTimeoutRef.current = window.setTimeout(() => {
      measureQuizHeightForId(initialQuizElementId);
    }, 100);

    return () => {
      window.removeEventListener("resize", measureQuizHeight);
      window.removeEventListener("quizExpandedChange", handleQuizChange);
      resizeObserver?.disconnect();

      if (initialMeasureTimeoutRef.current) {
        clearTimeout(initialMeasureTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      id={`trans-${id}`}
      className="flex flex-col items-center"
      style={{ marginBottom: `calc(${quizHeight}px + 2rem)` }}
    >
      <div className="flex justify-between items-center w-full h-[48px] lg:max-w-[860px] mx-auto bg-white-100 pl-[16px] pr-[8px] sticky top-0">
        <span className="text-blue-400 text-sm font-primary font-semimedium">
          Translation by Google Translate
        </span>
        <button className="" onClick={onClose}>
          <img
            src={TranslateModalClose}
            alt="translate-close"
            width={23}
            height={23}
          />
        </button>
      </div>
      <FieldWrapper>
        <div className="flex flex-col gap-xs">
          <TranslationImage sectionName={sectionName} alt={""} url={imageUrl} />
          <div className="flex flex-col gap-xs px-sm lg:px-0">
            <Heading
              Element="h2"
              className="font-secondary leading-2xl text-black-800 text-xl lg:text-[38px] font-medium"
            >
              {title}
            </Heading>
            {standFirst && (
              <StandFirst className="font-medium text-black-800 lg:text-[20px] my-3 lg:my-4">
                {standFirst}
              </StandFirst>
            )}
          </div>
        </div>
      </FieldWrapper>
      <FieldWrapper className="flex flex-col w-full gap-xs mt-[10px]">
        {body.map((para, index) => (
          <React.Fragment key={index}>
            {index === 2 && (
              <TranslationAdsImu1
                uniqueSectionName={adsSectionName}
                index={pageNumber}
                gsChannels={gsChannels}
                articleSlotTarget={
                  articleSlotTarget
                    ? [...articleSlotTarget, PubPageAdTarget]
                    : [PubPageAdTarget]
                }
              />
            )}
            {index === 6 && (
              <TranslationAdsMidArticle
                uniqueSectionName={adsSectionName}
                index={pageNumber}
                gsChannels={gsChannels}
                articleSlotTarget={
                  articleSlotTarget
                    ? [...articleSlotTarget, PubPageAdTarget]
                    : [PubPageAdTarget]
                }
              />
            )}
            <p
              dangerouslySetInnerHTML={{ __html: Dompurify.sanitize(para) }}
              className="text-sm font-primary font-semimedium text-black-800 leading-[24px] px-sm lg:px-0"
            />
          </React.Fragment>
        ))}
      </FieldWrapper>
    </div>
  );
}
